import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useEffect, useMemo, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { AppIcon } from '../../../../components/ui/icon'
import { mdiCheck, mdiClose, mdiPencil, mdiAlertOutline } from '@mdi/js'

import { useOrganization, queryKeys } from '../../../../hooks/useQueries'
import { CurrencySelect } from '../../../../components/listings/listingShared'
import { useUpdateOrganization } from '../../../../api/queries'
import { useBreadcrumbs } from '../../../../hooks/useBreadcrumbs'
import { useAuth, useIsPlatformAdmin } from '../../../../hooks/useAuth'
import { useCan } from '../../../../contexts/PermissionsContext'
import { EntityPicture } from '../../../../components/EntityPicture'
import { Button } from '../../../../components/ui/button'
import { Input } from '../../../../components/ui/input'
import { notify } from '../../../../lib/notifications'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../../../../components/ui/select'
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../../../../components/ui/alert-dialog'
import { PageHeader } from '../../../../components/shared/PageHeader'
import { JurisdictionSelect } from '../../../../components/JurisdictionSelect'
import { SECTION_ICONS } from '../../../../lib/section-icons'
import { LettingSettingsCard } from '../../../../components/settings/LettingSettingsCard'

export const Route = createFileRoute('/$org/settings/organization/')({
    component: OrganizationCoreSettingsPage,
})

/** Notification keys, so a repeat save replaces its own banner. */
const SAVED_KEY = 'organization_settings_saved'
const ERROR_KEY = 'organization_settings_error'

/** Error codes this page has a sentence for; anything else falls to `unknown`. */
const KNOWN_ERROR_CODES = [
    'invalid_locale',
    'INVALID_JURISDICTION',
    'invalid_currency',
    'invalid_organization_code',
    'organization_code_taken',
    'platform_admin_required',
    'bad_request',
]

/** The languages the `locale` organization setting may carry. */
const CONTENT_LOCALES = ['en', 'fr'] as const
type ContentLocale = (typeof CONTENT_LOCALES)[number]

/**
 * The organization's identity: its name, its slug, its logo and its language
 * (spec 2026-09-09-10 §4).
 *
 * Two of those four had no screen at all before this page: the slug was only
 * editable by hand in the database, and the language — which decides what
 * language AI summaries, generated room names and resident-facing copy come out
 * in — was only reachable through the generic settings endpoint. The settings
 * index, which used to carry the logo card, goes back to being a pure directory.
 */
function OrganizationCoreSettingsPage() {
    const { org } = Route.useParams()
    const { t } = useTranslation('settings')
    const navigate = useNavigate()
    const queryClient = useQueryClient()
    const isPlatformAdmin = useIsPlatformAdmin()
    const { updateAuthOrgCode } = useAuth()
    const canWrite = useCan('organization.write')

    const { data: organization, isLoading } = useOrganization(org)

    const breadcrumbs = useMemo(
        () => [
            { title: t('common:nav.home'), path: `/${org}` },
            { title: t('page.title'), path: `/${org}/settings` },
            { title: t('organization.breadcrumb') },
        ],
        [org, t]
    )
    useBreadcrumbs(breadcrumbs)

    // Name: click-to-edit, Enter saves, Escape cancels.
    const [editingName, setEditingName] = useState(false)
    const [nameDraft, setNameDraft] = useState('')
    // Slug: read-only for everyone; a platform admin edits it behind a
    // confirmation that spells out what a rename breaks.
    const [editingCode, setEditingCode] = useState(false)
    const [codeDraft, setCodeDraft] = useState('')
    const [confirmRename, setConfirmRename] = useState(false)
    // Jurisdiction: click-to-edit with a select, saved explicitly.
    const [editingJurisdiction, setEditingJurisdiction] = useState(false)
    const [jurisdictionDraft, setJurisdictionDraft] = useState('')

    useEffect(() => {
        if (organization) {
            setNameDraft(organization.name ?? '')
            setCodeDraft(organization.code ?? '')
        }
    }, [organization])

    // Saving goes through the app's notification bus rather than component
    // state: a save invalidates the organization query the whole app shell
    // reads, and a banner owned by this component would be wiped by the
    // re-render that follows its own success.
    const announceSaved = () => {
        notify({
            key: SAVED_KEY,
            variant: 'default',
            titleKey: 'settings:organization.saved',
        })
    }

    const announceError = (code: string | null) => {
        notify({
            key: ERROR_KEY,
            variant: 'destructive',
            titleKey: 'settings:organization.errors.title',
            descriptionKey: `settings:organization.errors.${code && KNOWN_ERROR_CODES.includes(code) ? code : 'unknown'}`,
            timeoutMs: 0,
        })
    }

    const mutation = useUpdateOrganization(org)

    const save = async (
        body: { name?: string; code?: string; locale?: ContentLocale; jurisdiction?: string; currency?: string }
    ): Promise<boolean> => {
        try {
            await mutation.mutateAsync(body)
            announceSaved()
            return true
        } catch (err) {
            announceError(parseErrorCode(err))
            return false
        }
    }

    const saveName = async () => {
        const next = nameDraft.trim()
        if (!next || next === organization?.name) {
            setEditingName(false)
            setNameDraft(organization?.name ?? '')
            return
        }
        if (await save({ name: next })) setEditingName(false)
    }

    const saveCurrency = async (currency: string) => {
        if (currency === organization?.currency) return
        await save({ currency })
    }

    const saveLocale = async (locale: string) => {
        if (!CONTENT_LOCALES.includes(locale as ContentLocale)) return
        await save({ locale: locale as ContentLocale })
    }

    const saveJurisdiction = async () => {
        const next = jurisdictionDraft.trim()
        if (!next || next === organization?.jurisdiction) {
            setEditingJurisdiction(false)
            return
        }
        if (await save({ jurisdiction: next })) setEditingJurisdiction(false)
    }

    const renameSlug = async () => {
        const next = codeDraft.trim()
        setConfirmRename(false)
        if (!next || next === organization?.code) {
            setEditingCode(false)
            setCodeDraft(organization?.code ?? '')
            return
        }
        if (await save({ code: next })) {
            setEditingCode(false)
            // Every URL just changed. Re-point the session's stored org code
            // and land the caller on this same page under the new slug —
            // staying put would 404 on the next request.
            updateAuthOrgCode(next)
            queryClient.invalidateQueries({ queryKey: queryKeys.organization(org) })
            navigate({ to: '/$org/settings/organization', params: { org: next } })
        }
    }

    if (isLoading) {
        return (
            <div className="container mx-auto py-8 max-w-3xl" data-testid="organization-settings-loading">
                <div className="h-8 w-64 bg-muted animate-pulse rounded" />
            </div>
        )
    }

    return (
        <div className="container mx-auto py-8 max-w-3xl" data-testid="organization-settings-page">
            <PageHeader
                className="mb-8"
                icon={SECTION_ICONS.settings}
                title={t('organization.title')}
                description={t('organization.subtitle')}
            />

            <div className="space-y-6">
                {/* Name */}
                <section className="bg-card rounded-lg border border-border p-6">
                    <h2 className="text-base font-semibold text-foreground">{t('organization.name.title')}</h2>
                    <p className="text-muted-foreground text-sm mt-1 mb-4">{t('organization.name.description')}</p>
                    {editingName ? (
                        <div className="flex items-center gap-2">
                            <Input
                                autoFocus
                                value={nameDraft}
                                onChange={(e) => setNameDraft(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault()
                                        void saveName()
                                    }
                                    if (e.key === 'Escape') {
                                        e.preventDefault()
                                        setEditingName(false)
                                        setNameDraft(organization?.name ?? '')
                                    }
                                }}
                                className="max-w-md"
                                data-testid="organization-name-input"
                            />
                            <Button
                                size="icon"
                                variant="outline"
                                onClick={() => void saveName()}
                                aria-label={t('organization.save')}
                                title={t('organization.save')}
                                data-testid="organization-name-save"
                            >
                                <AppIcon path={mdiCheck} size="md" />
                            </Button>
                            <Button
                                size="icon"
                                variant="outline"
                                onClick={() => {
                                    setEditingName(false)
                                    setNameDraft(organization?.name ?? '')
                                }}
                                aria-label={t('organization.cancel')}
                                title={t('organization.cancel')}
                                data-testid="organization-name-cancel"
                            >
                                <AppIcon path={mdiClose} size="md" />
                            </Button>
                        </div>
                    ) : (
                        <div className="flex items-center gap-3">
                            <span className="text-sm font-medium text-foreground" data-testid="organization-name-value">
                                {organization?.name || '—'}
                            </span>
                            {canWrite && (
                                <Button
                                    size="icon"
                                    variant="outline"
                                    onClick={() => setEditingName(true)}
                                    aria-label={t('organization.name.edit')}
                                    title={t('organization.name.edit')}
                                    data-testid="organization-name-edit"
                                >
                                    <AppIcon path={mdiPencil} size="sm" />
                                </Button>
                            )}
                        </div>
                    )}
                </section>

                {/* Slug */}
                <section className="bg-card rounded-lg border border-border p-6">
                    <h2 className="text-base font-semibold text-foreground">{t('organization.slug.title')}</h2>
                    <p className="text-muted-foreground text-sm mt-1 mb-4">{t('organization.slug.description')}</p>
                    {editingCode ? (
                        <div className="flex items-center gap-2">
                            <Input
                                autoFocus
                                value={codeDraft}
                                onChange={(e) => setCodeDraft(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault()
                                        setConfirmRename(true)
                                    }
                                    if (e.key === 'Escape') {
                                        e.preventDefault()
                                        setEditingCode(false)
                                        setCodeDraft(organization?.code ?? '')
                                    }
                                }}
                                className="max-w-md font-mono"
                                data-testid="organization-slug-input"
                            />
                            <Button
                                size="icon"
                                variant="outline"
                                onClick={() => setConfirmRename(true)}
                                aria-label={t('organization.save')}
                                title={t('organization.save')}
                                data-testid="organization-slug-save"
                            >
                                <AppIcon path={mdiCheck} size="md" />
                            </Button>
                            <Button
                                size="icon"
                                variant="outline"
                                onClick={() => {
                                    setEditingCode(false)
                                    setCodeDraft(organization?.code ?? '')
                                }}
                                aria-label={t('organization.cancel')}
                                title={t('organization.cancel')}
                                data-testid="organization-slug-cancel"
                            >
                                <AppIcon path={mdiClose} size="md" />
                            </Button>
                        </div>
                    ) : (
                        <div className="flex items-center gap-3">
                            <code
                                className="text-sm font-mono px-2 py-1 rounded bg-muted text-foreground"
                                data-testid="organization-slug-value"
                            >
                                {organization?.code}
                            </code>
                            {isPlatformAdmin && (
                                <Button
                                    size="icon"
                                    variant="outline"
                                    onClick={() => setEditingCode(true)}
                                    aria-label={t('organization.slug.edit')}
                                    title={t('organization.slug.edit')}
                                    data-testid="organization-slug-edit"
                                >
                                    <AppIcon path={mdiPencil} size="sm" />
                                </Button>
                            )}
                        </div>
                    )}
                    {!isPlatformAdmin && (
                        <p className="text-xs text-muted-foreground mt-3" data-testid="organization-slug-locked">
                            {t('organization.slug.locked')}
                        </p>
                    )}
                </section>

                {/* Logo */}
                <section className="bg-card rounded-lg border border-border p-6">
                    <h2 className="text-base font-semibold text-foreground">{t('organization.logo.title')}</h2>
                    <p className="text-muted-foreground text-sm mt-1 mb-4">{t('organization.logo.description')}</p>
                    <div className="flex items-start gap-4" data-testid="organization-logo">
                        <EntityPicture
                            entityType="organization"
                            entityId={organization?.uid || org}
                            documentUid={organization?.picture?.document_uid}
                            orgCode={org}
                            size="128x128"
                            editable={canWrite}
                            className="w-20 h-20"
                            alt={`${organization?.name || 'Organization'} picture`}
                            onUpload={(success) => {
                                if (success) {
                                    queryClient.invalidateQueries({ queryKey: queryKeys.organization(org) })
                                    queryClient.invalidateQueries({
                                        predicate: (query) => {
                                            const key = query.queryKey as any[]
                                            return key.length >= 2 && key[0] === 'get' &&
                                                key[1] === '/api/v1/organizations/{orgCode}'
                                        },
                                    })
                                    // Fires on both upload and delete (EntityPicture calls
                                    // onUpload(true) after either) — the sidebar brand reads
                                    // this same membership summary, so it must not keep
                                    // showing the old (or a stale missing) logo.
                                    queryClient.invalidateQueries({ queryKey: ['my-organizations'] })
                                    announceSaved()
                                }
                            }}
                        />
                        <div className="text-sm text-muted-foreground">
                            <p>{t('picture.click_to_upload')}</p>
                            <p className="mt-1">{t('picture.formats')}</p>
                        </div>
                    </div>
                </section>

                {/* Language */}
                <section className="bg-card rounded-lg border border-border p-6">
                    <h2 className="text-base font-semibold text-foreground">{t('organization.language.title')}</h2>
                    <p className="text-muted-foreground text-sm mt-1 mb-4">{t('organization.language.description')}</p>
                    <Select
                        value={organization?.locale ?? 'en'}
                        onValueChange={(value) => void saveLocale(value)}
                        disabled={!canWrite}
                    >
                        <SelectTrigger className="max-w-md" data-testid="organization-language-select">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {CONTENT_LOCALES.map((code) => (
                                <SelectItem key={code} value={code} data-testid={`organization-language-option-${code}`}>
                                    {t(`organization.language.options.${code}`)}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground mt-3" data-testid="organization-language-hint">
                        {t('organization.language.not_ui_language')}
                    </p>
                </section>

                {/* Default currency */}
                <section className="bg-card rounded-lg border border-border p-6" data-testid="organization-currency">
                    <h2 className="text-base font-semibold text-foreground">{t('organization.currency.title')}</h2>
                    <p className="text-muted-foreground text-sm mt-1 mb-4">{t('organization.currency.description')}</p>
                    <CurrencySelect
                        value={organization?.currency ?? 'EUR'}
                        onChange={(value) => void saveCurrency(value)}
                        disabled={!canWrite}
                        testId="organization-currency-select"
                    />
                </section>

                {/* Jurisdiction */}
                <section className="bg-card rounded-lg border border-border p-6" data-testid="organization-jurisdiction">
                    <h2 className="text-base font-semibold text-foreground">{t('organization.jurisdiction.title')}</h2>
                    <p className="text-muted-foreground text-sm mt-1 mb-4">{t('organization.jurisdiction.description')}</p>
                    {editingJurisdiction ? (
                        <div className="flex items-center gap-2">
                            <JurisdictionSelect
                                value={jurisdictionDraft}
                                onValueChange={setJurisdictionDraft}
                                includeAll={false}
                                className="max-w-md"
                                data-testid="organization-jurisdiction-select"
                            />
                            <Button
                                size="icon"
                                variant="outline"
                                onClick={() => void saveJurisdiction()}
                                disabled={!jurisdictionDraft}
                                aria-label={t('organization.save')}
                                title={t('organization.save')}
                                data-testid="organization-jurisdiction-save"
                            >
                                <AppIcon path={mdiCheck} size="md" />
                            </Button>
                            <Button
                                size="icon"
                                variant="outline"
                                onClick={() => setEditingJurisdiction(false)}
                                aria-label={t('organization.cancel')}
                                title={t('organization.cancel')}
                                data-testid="organization-jurisdiction-cancel"
                            >
                                <AppIcon path={mdiClose} size="md" />
                            </Button>
                        </div>
                    ) : (
                        <div className="flex items-center gap-3">
                            <code
                                className="text-sm font-mono px-2 py-1 rounded bg-muted text-foreground"
                                data-testid="organization-jurisdiction-value"
                            >
                                {organization?.jurisdiction || t('organization.jurisdiction.unset')}
                            </code>
                            {canWrite && (
                                <Button
                                    size="icon"
                                    variant="outline"
                                    onClick={() => {
                                        setJurisdictionDraft(organization?.jurisdiction ?? '')
                                        setEditingJurisdiction(true)
                                    }}
                                    aria-label={t('organization.jurisdiction.edit')}
                                    title={t('organization.jurisdiction.edit')}
                                    data-testid="organization-jurisdiction-edit"
                                >
                                    <AppIcon path={mdiPencil} size="sm" />
                                </Button>
                            )}
                        </div>
                    )}
                    <p className="text-xs text-muted-foreground mt-3" data-testid="organization-jurisdiction-hint">
                        {t('organization.jurisdiction.hint')}
                    </p>
                </section>

                <LettingSettingsCard org={org} canWrite={canWrite} />
            </div>

            <AlertDialog open={confirmRename} onOpenChange={setConfirmRename}>
                <AlertDialogContent data-testid="organization-slug-confirm">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="flex items-center gap-2">
                            <AppIcon path={mdiAlertOutline} size="md" className="text-destructive" />
                            {t('organization.slug.confirm_title')}
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            {t('organization.slug.confirm_description', {
                                from: organization?.code ?? '',
                                to: codeDraft.trim(),
                            })}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="organization-slug-confirm-cancel">
                            {t('organization.cancel')}
                        </AlertDialogCancel>
                        <AlertDialogAction
                            onClick={() => void renameSlug()}
                            data-testid="organization-slug-confirm-accept"
                        >
                            {t('organization.slug.confirm_accept')}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    )
}

/**
 * The error body's `code`, which the page turns into a sentence. The mutation
 * serialises the typed error into `Error.message`, so this reads it back.
 */
function parseErrorCode(err: unknown): string | null {
    if (!err) return null
    const text = err instanceof Error ? err.message : String(err)
    try {
        const parsed = JSON.parse(text)
        if (parsed && typeof parsed.code === 'string') return parsed.code
    } catch {
        // not JSON — fall through
    }
    return null
}
